import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import Section, { Eyebrow, SectionHeading } from "../common/Section";
import Reveal from "../common/Reveal";
import { CONTACT } from "../../config/site";
import { SHOW_CASE_STUDY_METRICS } from "../../config/claims";
import {
  SERVICES,
  SERVICE_CATEGORIES,
  servicesByCategory,
  technologyIndex,
} from "../../data/serviceCatalog";
import "./Css/WhychoosUs.css";

/**
 * Why DepthLogix — the homepage trust section.
 *
 * Each point is tied to a page on this site that backs it up, so the
 * section argues with evidence rather than adjectives. The counts below
 * are derived from the real service catalog at module scope, which means
 * they cannot drift out of date and cost nothing per render.
 */
const TOTAL_SERVICES = SERVICES.length;
const TOTAL_PRACTICES = SERVICE_CATEGORIES.length;
const BUILD_DISCIPLINES = servicesByCategory("digital-transformation").length;
const BUSINESS_PLATFORMS = servicesByCategory("business-apps").length;
const TOTAL_TECHNOLOGIES = technologyIndex().length;

/**
 * The owner's eight strengths, in the owner's order. `proof` is the page
 * on this site that substantiates the point — every path is verified
 * against src/data/serviceCatalog.js.
 */
const REASONS = [
  {
    title: "Custom-built software",
    body: "Work starts from your requirements, not from a template. The build practice covers web, mobile, custom software, UI/UX and SaaS as separate disciplines, each with its own scope and timeline.",
    meta: `${BUILD_DISCIPLINES} build disciplines`,
    proof: { label: "Custom Software", to: "/services/DigitalTransformation/custom-software" },
  },
  {
    title: "Direct communication",
    body: "You talk to the people doing the work. Email, phone and WhatsApp are all published, and all three reach the same team.",
    meta: CONTACT.email,
    // A real address reads better in lowercase than in the uppercase
    // micro-label style the other rows use.
    metaLowercase: true,
    proof: { label: "Contact", to: "/contact" },
  },
  {
    title: "Production-ready development",
    body: "Performance optimization and security implementation are listed deliverables on the Web Development page, not extras discovered at the end of a build.",
    meta: "4-12 weeks · Web Development",
    proof: { label: "Web Development", to: "/services/DigitalTransformation/web-development" },
  },
  {
    title: "Scalable architecture",
    body: "Multi-tenant architecture, subscription billing and scalable infrastructure are written into the SaaS scope, and the same patterns carry into custom builds on AWS, Azure, Docker and Kubernetes.",
    meta: "12-36 weeks · SaaS Development",
    proof: { label: "SaaS Development", to: "/services/DigitalTransformation/SaaS" },
  },
  {
    title: "Business-focused solutions",
    body: "ERP and CRM work is scoped around finance, supply chain, sales and service processes: Dynamics 365 ERP and CRM, Power Apps, Salesforce and Odoo.",
    meta: `${BUSINESS_PLATFORMS} business platforms`,
    proof: { label: "Dynamics 365 ERP", to: "/services/BusinessApp/dynamics-365-erp" },
  },
  {
    title: "Long-term support",
    body: "Maintenance is a service with its own page rather than a clause at the bottom of a contract: security updates, backups, content updates and uptime monitoring, on an ongoing basis.",
    meta: "Ongoing engagement",
    proof: { label: "Maintenance & Support", to: "/services/DigitalMarketing/maintenance-&-support" },
  },
  {
    title: "Modern technologies",
    body: "Every service page names the stack it is built on: React, Node.js, Python, .NET Core, Azure and the Power Platform among them, so you can check the fit before the first call.",
    meta: `${TOTAL_TECHNOLOGIES} technologies named`,
    proof: { label: `All ${TOTAL_SERVICES} services`, to: "/services" },
  },
  {
    title: "Attention to detail",
    body: "Usability, responsiveness, performance and polish get checked before launch. Design systems and usability testing sit inside the design scope, not beside it.",
    meta: "Design systems · Usability testing",
    proof: { label: "UI/UX Design", to: "/services/DigitalTransformation/ui-ux-design" },
  },
];

/**
 * The original "Featured Insights" cards — the owner's copy, kept intact.
 *
 * Four of these are badged "Case Study" and quote specific client outcomes
 * (100K monthly transactions, $300M+ reconciled, 55% growth, $2M+ funding)
 * with no named client, date or linked write-up; the four "Blogs" titles
 * do not correspond to any post on /blog. They are therefore gated behind
 * SHOW_CASE_STUDY_METRICS (src/config/claims.js) until there is a real
 * page behind each one. Nothing has been deleted — flip the flag and this
 * block returns. The eight Unsplash stock photographs that used to sit
 * behind them are not coming back either way.
 */
const FEATURED_INSIGHTS = [
  { type: "Case Study", title: "US Fashion Resale Platform Scales to 100K Monthly Transactions" },
  { type: "Blogs", title: "How Cloud Computing Can Transform Small Businesses" },
  { type: "Blogs", title: "Custom Web Application Development: Everything You Need to Know" },
  { type: "Blogs", title: "Trends of Mobile Design: What's Next for Your Business?" },
  { type: "Blogs", title: "How Generative AI is Transforming Business Operations" },
  { type: "Case Study", title: "Hospitality AI Platform Reconciles $300M+ OTA Commissions Automatically" },
  { type: "Case Study", title: "Pakistan Furniture Leaders Shopify Migration Drives 55% Growth" },
  { type: "Case Study", title: "US Fintech's AI Financial Modeling Secures $2M+ Funding" },
];

/** Below this width the list collapses to its first few rows. */
const COMPACT_QUERY = "(max-width: 767px)";
const COMPACT_COUNT = 4;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * True while the viewport is narrow enough to warrant progressive
 * disclosure. Driven by matchMedia rather than a resize handler, so it
 * re-renders once when the breakpoint is crossed instead of on every
 * resize frame.
 */
const useCompactViewport = () => {
  const [isCompact, setIsCompact] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(COMPACT_QUERY).matches
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mql = window.matchMedia(COMPACT_QUERY);
    const onChange = (event) => setIsCompact(event.matches);
    setIsCompact(mql.matches);

    // addEventListener on MediaQueryList is unsupported in Safari < 14.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isCompact;
};

const WhyChooseUs = () => {
  const isCompact = useCompactViewport();
  const [isExpanded, setIsExpanded] = useState(false);
  const listRef = useRef(null);
  const justCollapsed = useRef(false);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((expanded) => {
      justCollapsed.current = expanded;
      return !expanded;
    });
  }, []);

  // Collapsing removes rows above the button, so bring the top of the list
  // back into view if it has scrolled off. Runs after the DOM has updated.
  useEffect(() => {
    if (!justCollapsed.current) return;
    justCollapsed.current = false;

    const node = listRef.current;
    if (!node || node.getBoundingClientRect().top >= 0) return;
    node.scrollIntoView({
      block: "start",
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [isExpanded]);

  const collapsed = isCompact && !isExpanded;
  const visibleReasons = collapsed ? REASONS.slice(0, COMPACT_COUNT) : REASONS;

  return (
    <Section id="why-depthlogix" tone="subtle" className="dl-why">
      <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 xl:gap-24">
        {/* The argument. Sticks while the evidence scrolls past it —
            pure CSS, no scroll listener. */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <SectionHeading
            eyebrow="Why DepthLogix"
            title="Eight reasons, each one you can check."
            lead="Every point on the right links to the service page, the stack or the contact channel that backs it. Nothing here rests on a figure we cannot show you."
            maxWidth="max-w-xl"
          />

          <Reveal delay={90} className="mt-10">
            <dl className="grid grid-cols-3 gap-px overflow-hidden rounded-card border border-ink-200 bg-ink-200">
              {[
                { value: TOTAL_SERVICES, label: "Services published" },
                { value: TOTAL_PRACTICES, label: "Practice areas" },
                { value: TOTAL_TECHNOLOGIES, label: "Technologies named" },
              ].map((stat) => (
                // Reversed column so the figure reads first visually while
                // the DOM keeps the label-then-value order screen readers want.
                <div
                  key={stat.label}
                  className="flex flex-col-reverse bg-white px-3 py-4 sm:px-4 sm:py-5"
                >
                  <dt className="mt-1 text-xs leading-snug text-ink-500">
                    {stat.label}
                  </dt>
                  <dd className="font-mono text-2xl font-medium tabular-nums text-brand-800">
                    {stat.value}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-xs text-ink-400">
              Counted from this site&apos;s own service pages.
            </p>
          </Reveal>

          <Reveal delay={150} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            <Link to="/services" className="btn-secondary">
              Browse the service catalog
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 underline-offset-4 transition-colors duration-200 hover:text-brand-500 hover:underline"
            >
              How we work
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>

        {/* The evidence. */}
        <div>
          <ol id="why-depthlogix-evidence" ref={listRef} className="list-none">
            {visibleReasons.map((reason, index) => (
              <Reveal
                as="li"
                key={reason.title}
                delay={Math.min(index, 4) * 70}
                className="dl-why__row group relative py-7 pl-5 sm:pl-7"
              >
                <div className="flex items-baseline gap-4 sm:gap-6">
                  <span
                    aria-hidden="true"
                    className="dl-why__index font-mono text-xs text-brand-600/70"
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bricolage text-lg font-semibold tracking-[-0.01em] text-ink-900 sm:text-xl">
                      {reason.title}
                    </h3>
                    <p className="mt-2 max-w-[54ch] text-[0.95rem] leading-relaxed text-ink-500 text-pretty">
                      {reason.body}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                      <span
                        className={`font-mono text-[0.68rem] tracking-[0.12em] text-ink-400 ${
                          reason.metaLowercase ? "" : "uppercase"
                        }`}
                      >
                        {reason.meta}
                      </span>
                      <Link
                        to={reason.proof.to}
                        className="dl-why__proof inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 transition-colors duration-200 group-hover:text-brand-500 sm:ml-auto"
                      >
                        {reason.proof.label}
                        <ArrowUpRight
                          className="h-3.5 w-3.5 transition-transform duration-200 ease-out-expo group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          aria-hidden="true"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </ol>

          {isCompact && (
            <button
              type="button"
              onClick={toggleExpanded}
              aria-expanded={isExpanded}
              aria-controls="why-depthlogix-evidence"
              className="btn-secondary mt-8 w-full"
            >
              {isExpanded
                ? `Show the first ${COMPACT_COUNT} reasons`
                : `Show all ${REASONS.length} reasons`}
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ease-out-expo ${
                  isExpanded ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </button>
          )}

          {/* Off until each headline below has a real page behind it —
              see FEATURED_INSIGHTS above and src/config/claims.js. */}
          {SHOW_CASE_STUDY_METRICS && (
            <Reveal className="mt-16 border-t border-ink-200 pt-10">
              <Eyebrow>Featured Insights</Eyebrow>
              <h3 className="mt-4 font-bricolage text-2xl font-bold text-ink-900">
                Across Industries, Across Services: Tales of Transformation
              </h3>
              <p className="mt-2 text-ink-500">From Concept to Completion</p>
              <ul className="mt-6 list-none">
                {FEATURED_INSIGHTS.map((item) => (
                  <li
                    key={item.title}
                    className="border-t border-ink-200 py-4 first:border-t-0"
                  >
                    <span className="font-mono text-[0.68rem] uppercase tracking-[0.12em] text-ink-400">
                      {item.type}
                    </span>
                    <p className="mt-1 text-base text-ink-800">{item.title}</p>
                  </li>
                ))}
              </ul>
            </Reveal>
          )}
        </div>
      </div>
    </Section>
  );
};

export default WhyChooseUs;
